import { useEffect, useEffectEvent, useMemo, useRef, useState, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSocketContext } from '@/contexts/socket-context'
import { getApiErrorMessage } from '@/lib/api-error'

/** Block shape rendered in the UI — server persists a superset, but these are all we show. */
export type UIBlock =
  | { type: 'text'; text: string }
  | { type: 'thinking'; text: string; open?: boolean }
  | {
      type: 'tool_use'
      tool_use_id: string
      name: string
      label: string
      input_preview?: string
      status: 'running' | 'ok' | 'error'
      summary?: string
    }
  | {
      type: 'attachment'
      attachment_uuid: string
      filename: string
      content_type?: string | null
      size_bytes?: number
      is_image?: boolean
      is_pdf?: boolean
    }

/** Sent alongside user message text — references uploaded AgentAttachment rows. */
export interface SendAttachment {
  attachment_uuid: string
  filename: string
  content_type?: string | null
  size_bytes?: number
  is_image?: boolean
  is_pdf?: boolean
}

export interface UIMessage {
  uuid: string
  role: 'user' | 'assistant'
  blocks: UIBlock[]
  status: 'streaming' | 'complete' | 'errored' | 'cancelled'
  clientRequestId?: string | null
}

export type AgentToolResultEvent = {
  conversation_uuid: string
  message_uuid: string
  tool_use_id: string
  name: string
  ok: boolean
  summary?: string
}

const lastBlock = (msg: UIMessage) => msg.blocks[msg.blocks.length - 1]
const conversationQueryKey = (conversationUuid: string) =>
  ['agent', 'conversation', conversationUuid] as const

export type StoredAgentMessage = {
  uuid: string
  role: string
  content_blocks: Array<Record<string, unknown>>
  status?: string
  client_request_id?: string | null
}

type CachedConversation = {
  messages: StoredAgentMessage[]
}

type CorrelatedAgentEvent = {
  // New servers echo client_request_id on message_start/error. request_id and
  // missing IDs remain accepted while the socket contract rolls out.
  client_request_id?: string
  request_id?: string
}

type PendingSend = {
  requestId: string
  optimisticUuid: string
  resolve: (accepted: boolean) => void
}

type RetainedSend = {
  requestId: string
  snapshot: string
  authoritative: boolean
}

const RETAINED_SEND_STORAGE_PREFIX = 'usars-agent-retained-send:'
const retainedSendMemory = new Map<string, RetainedSend>()

function eventRequestId(event: CorrelatedAgentEvent): string | undefined {
  return event.client_request_id ?? event.request_id
}

function clientRequestId(sequence: number): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `agent-send-${Date.now()}-${sequence}-${Math.random().toString(36).slice(2, 10)}`
  )
}

function sendSnapshot(content: string, attachments: SendAttachment[]): string {
  return JSON.stringify({
    content,
    attachmentUuids: attachments.map((attachment) => attachment.attachment_uuid),
  })
}

function retainedSendStorageKey(conversationUuid: string): string {
  return `${RETAINED_SEND_STORAGE_PREFIX}${conversationUuid}`
}

function loadRetainedSend(conversationUuid: string | null): RetainedSend | null {
  if (!conversationUuid || typeof window === 'undefined') return null
  const key = retainedSendStorageKey(conversationUuid)
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) {
      retainedSendMemory.delete(key)
      return null
    }
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      retainedSendMemory.delete(key)
      return null
    }
    const retained = parsed as Record<string, unknown>
    if (
      typeof retained.requestId !== 'string' ||
      typeof retained.snapshot !== 'string' ||
      typeof retained.authoritative !== 'boolean'
    ) {
      retainedSendMemory.delete(key)
      return null
    }
    const result = {
      requestId: retained.requestId,
      snapshot: retained.snapshot,
      authoritative: retained.authoritative,
    }
    retainedSendMemory.set(key, result)
    return result
  } catch {
    return retainedSendMemory.get(key) ?? null
  }
}

function persistRetainedSend(
  conversationUuid: string | null,
  retained: RetainedSend | null,
): void {
  if (!conversationUuid || typeof window === 'undefined') return
  const key = retainedSendStorageKey(conversationUuid)
  if (retained) retainedSendMemory.set(key, retained)
  else retainedSendMemory.delete(key)
  try {
    if (retained) {
      window.sessionStorage.setItem(key, JSON.stringify(retained))
    } else {
      window.sessionStorage.removeItem(key)
    }
  } catch {
    // Locked-down storage still retains the receipt for the mounted hook.
  }
}

function interruptMessage(msg: UIMessage): UIMessage {
  const blocks = msg.blocks.map((block): UIBlock => {
    if (block.type === 'thinking') return { ...block, open: true }
    if (block.type === 'tool_use' && block.status === 'running') {
      return {
        ...block,
        status: 'error',
        summary: block.summary ?? 'Connection interrupted before the tool result was received.',
      }
    }
    return block
  })
  return {
    ...msg,
    blocks:
      blocks.length > 0
        ? blocks
        : [
            {
              type: 'text',
              text: 'Connection interrupted before this response completed.',
            },
          ],
    status: 'errored',
  }
}

function failMessage(msg: UIMessage, error: string): UIMessage {
  const blocks = msg.blocks.map((block): UIBlock => {
    if (block.type === 'thinking') return { ...block, open: true }
    if (block.type === 'tool_use' && block.status === 'running') {
      return {
        ...block,
        status: 'error',
        summary: block.summary ?? 'Interrupted before the tool result was received.',
      }
    }
    return block
  })
  return {
    ...msg,
    blocks: [...blocks, { type: 'text', text: `Error: ${error}` }],
    status: 'errored',
  }
}

function cachedMessages(conversation: CachedConversation): UIMessage[] {
  return storedMessagesToUi(conversation.messages)
}

function historyCoversLiveResponses(history: UIMessage[], live: UIMessage[]): boolean {
  const persistedIds = new Set(history.map((message) => message.uuid))
  return live.every(
    (message) =>
      message.role !== 'assistant' ||
      message.status === 'errored' ||
      message.uuid.startsWith('pending-') ||
      message.uuid.startsWith('err-') ||
      persistedIds.has(message.uuid),
  )
}

function reconcilePersistedHistory(history: UIMessage[], live: UIMessage[]): UIMessage[] {
  const localFailures = new Map(
    live
      .filter((message) => message.role === 'assistant' && message.status === 'errored')
      .map((message) => [message.uuid, message]),
  )
  const reconciled = history.map((message) => localFailures.get(message.uuid) ?? message)
  const historyIds = new Set(history.map((message) => message.uuid))
  for (const failure of localFailures.values()) {
    if (!historyIds.has(failure.uuid)) reconciled.push(failure)
  }
  return reconciled
}

function appendDelta(msg: UIMessage, kind: 'text' | 'thinking', delta: string): UIMessage {
  const last = lastBlock(msg)
  if (last && last.type === kind) {
    const updated = {
      ...last,
      text: (last as { text: string }).text + delta,
    } as UIBlock
    return { ...msg, blocks: [...msg.blocks.slice(0, -1), updated] }
  }
  return {
    ...msg,
    blocks: [
      ...msg.blocks,
      kind === 'text' ? { type: 'text', text: delta } : { type: 'thinking', text: delta },
    ],
  }
}

function replaceToolUse(
  msg: UIMessage,
  tool_use_id: string,
  patch: (b: Extract<UIBlock, { type: 'tool_use' }>) => UIBlock,
): UIMessage {
  return {
    ...msg,
    blocks: msg.blocks.map((b) =>
      b.type === 'tool_use' && b.tool_use_id === tool_use_id ? patch(b) : b,
    ),
  }
}

interface UseAgentChatOptions {
  conversationUuid: string | null
  /** Seed messages from loaded history */
  initialMessages?: UIMessage[]
  onToolResult?: (event: AgentToolResultEvent) => void
}

export function useAgentChat({
  conversationUuid,
  initialMessages,
  onToolResult,
}: UseAgentChatOptions) {
  const { on, emit, emitWithAuth, isConnected } = useSocketContext()
  const qc = useQueryClient()
  // NOTE: the caller is expected to remount this hook when `conversationUuid`
  // changes (via React `key`), so initialMessages becomes the initial state
  // on each fresh mount and we avoid setState-inside-effect entirely.
  const [messages, setMessages] = useState<UIMessage[]>(initialMessages ?? [])
  const [isStreaming, setIsStreaming] = useState(false)
  const [appliedInitialMessages, setAppliedInitialMessages] = useState(initialMessages)
  const isStreamingRef = useRef(false)
  const currentMsgUuid = useRef<string | null>(null)
  const needsHistoryRecovery = useRef(!isConnected)
  const transcriptVersion = useRef(0)
  const cancelRequested = useRef(false)
  const localMessageSequence = useRef(0)
  const pendingSend = useRef<PendingSend | null>(null)
  const retainedSend = useRef<RetainedSend | null>(loadRetainedSend(conversationUuid))
  const activeRequestId = useRef<string | null>(null)
  const notifyToolResult = useEffectEvent((event: AgentToolResultEvent) => {
    onToolResult?.(event)
  })

  if (
    initialMessages !== appliedInitialMessages &&
    !isStreaming &&
    historyCoversLiveResponses(initialMessages ?? [], messages)
  ) {
    setAppliedInitialMessages(initialMessages)
    setMessages(reconcilePersistedHistory(initialMessages ?? [], messages))
  }

  useEffect(() => {
    const retained = retainedSend.current
    if (
      retained &&
      initialMessages?.some(
        (message) =>
          message.role === 'user' &&
          message.clientRequestId === retained.requestId &&
          message.status !== 'streaming',
      )
    ) {
      retained.authoritative = true
      persistRetainedSend(conversationUuid, retained)
    }
  }, [conversationUuid, initialMessages])

  useEffect(() => {
    const offs: Array<() => void> = []
    let active = true

    offs.push(
      on('disconnect', () => {
        transcriptVersion.current += 1
        needsHistoryRecovery.current = true
        cancelRequested.current = false
        const pending = pendingSend.current
        pendingSend.current = null
        if (pending && retainedSend.current?.requestId === pending.requestId) {
          retainedSend.current.authoritative = false
          persistRetainedSend(conversationUuid, retainedSend.current)
        }
        pending?.resolve(false)
        const interruptedUuid = currentMsgUuid.current
        currentMsgUuid.current = null
        activeRequestId.current = null
        isStreamingRef.current = false
        setIsStreaming(false)
        if (interruptedUuid || pending) {
          setMessages((prev) =>
            prev
              .filter((message) => message.uuid !== pending?.optimisticUuid)
              .map((message) =>
                message.uuid === interruptedUuid ? interruptMessage(message) : message,
              ),
          )
        }
      }),
    )

    offs.push(
      on('connect', () => {
        if (!needsHistoryRecovery.current || !conversationUuid) return
        needsHistoryRecovery.current = false
        const recoveryVersion = transcriptVersion.current
        const queryKey = conversationQueryKey(conversationUuid)
        void qc.invalidateQueries({ queryKey, exact: true, refetchType: 'active' }).then(() => {
          if (!active || transcriptVersion.current !== recoveryVersion) return
          const state = qc.getQueryState<CachedConversation>(queryKey)
          if (state?.status === 'success' && state.data) {
            const recovered = cachedMessages(state.data)
            setMessages((prev) =>
              transcriptVersion.current === recoveryVersion ? recovered : prev,
            )
          }
        })
      }),
    )

    offs.push(
      on(
        'agent:message_start',
        (ev: {
          message_uuid: string
          conversation_uuid: string
          client_request_id?: string
          request_id?: string
        }) => {
          if (!conversationUuid || ev.conversation_uuid !== conversationUuid) return
          const pending = pendingSend.current
          const requestId = eventRequestId(ev)
          if (pending && requestId && requestId !== pending.requestId) return
          if (!pending && requestId && requestId !== activeRequestId.current) return
          if (pending) {
            pendingSend.current = null
            activeRequestId.current = pending.requestId
            if (retainedSend.current?.requestId === pending.requestId) {
              retainedSend.current.authoritative = false
              persistRetainedSend(conversationUuid, retainedSend.current)
            }
            pending.resolve(true)
          }
          transcriptVersion.current += 1
          currentMsgUuid.current = ev.message_uuid
          isStreamingRef.current = true
          setIsStreaming(true)
          setMessages((prev) => [
            ...prev,
            {
              uuid: ev.message_uuid,
              role: 'assistant',
              blocks: [],
              status: 'streaming',
            },
          ])
          if (cancelRequested.current) {
            emit('agent:cancel', { conversation_uuid: conversationUuid })
          }
        },
      ),
    )

    offs.push(
      on(
        'agent:text_delta',
        (ev: { conversation_uuid: string; message_uuid: string; delta: string }) => {
          if (!conversationUuid || ev.conversation_uuid !== conversationUuid) return
          const activeUuid = currentMsgUuid.current
          if (ev.message_uuid !== activeUuid) return
          transcriptVersion.current += 1
          setMessages((prev) =>
            prev.map((m) => (m.uuid === activeUuid ? appendDelta(m, 'text', ev.delta) : m)),
          )
        },
      ),
    )

    offs.push(
      on(
        'agent:thinking_delta',
        (ev: { conversation_uuid: string; message_uuid: string; delta: string }) => {
          if (!conversationUuid || ev.conversation_uuid !== conversationUuid) return
          const activeUuid = currentMsgUuid.current
          if (ev.message_uuid !== activeUuid) return
          transcriptVersion.current += 1
          setMessages((prev) =>
            prev.map((m) => (m.uuid === activeUuid ? appendDelta(m, 'thinking', ev.delta) : m)),
          )
        },
      ),
    )

    offs.push(
      on(
        'agent:tool_use',
        (ev: {
          conversation_uuid: string
          message_uuid: string
          tool_use_id: string
          name: string
          label: string
          input_preview?: string
        }) => {
          if (!conversationUuid || ev.conversation_uuid !== conversationUuid) return
          const activeUuid = currentMsgUuid.current
          if (ev.message_uuid !== activeUuid) return
          transcriptVersion.current += 1
          setMessages((prev) =>
            prev.map((m) =>
              m.uuid === activeUuid
                ? {
                    ...m,
                    blocks: [
                      ...m.blocks,
                      {
                        type: 'tool_use',
                        tool_use_id: ev.tool_use_id,
                        name: ev.name,
                        label: ev.label,
                        input_preview: ev.input_preview,
                        status: 'running',
                      },
                    ],
                  }
                : m,
            ),
          )
        },
      ),
    )

    offs.push(
      on('agent:tool_result', (ev: AgentToolResultEvent) => {
        if (!conversationUuid || ev.conversation_uuid !== conversationUuid) return
        const activeUuid = currentMsgUuid.current
        if (ev.message_uuid !== activeUuid) return
        transcriptVersion.current += 1
        setMessages((prev) =>
          prev.map((m) =>
            m.uuid === activeUuid
              ? replaceToolUse(m, ev.tool_use_id, (b) => ({
                  ...b,
                  status: ev.ok ? 'ok' : 'error',
                  summary: ev.summary,
                }))
              : m,
          ),
        )
        notifyToolResult(ev)
      }),
    )

    offs.push(
      on(
        'agent:message_stop',
        (ev: {
          conversation_uuid: string
          message_uuid: string
          stop_reason?: string
          status: 'complete' | 'cancelled'
          client_request_id?: string
          request_id?: string
        }) => {
          if (!conversationUuid || ev.conversation_uuid !== conversationUuid) return
          const requestId = eventRequestId(ev)
          if (requestId && requestId !== activeRequestId.current) return
          const placeholder = currentMsgUuid.current
          if (!placeholder) {
            if (ev.status === 'cancelled' || ev.stop_reason !== 'tool_use') {
              cancelRequested.current = false
            }
            return
          }
          if (!placeholder.startsWith('pending-') && placeholder !== ev.message_uuid) return

          transcriptVersion.current += 1
          // The server has persisted the message; refresh the cache so next
          // load / resume is authoritative, but don't block the UI on it.
          qc.invalidateQueries({
            queryKey: conversationQueryKey(conversationUuid),
          })
          setMessages((prev) => {
            const retained =
              ev.status === 'complete' && ev.stop_reason !== 'tool_use'
                ? prev.filter(
                    (message) =>
                      message.role !== 'assistant' ||
                      message.status !== 'errored' ||
                      message.uuid === placeholder,
                  )
                : prev
            return retained.map((m) =>
              m.uuid === placeholder || m.uuid === ev.message_uuid
                ? { ...m, uuid: ev.message_uuid, status: ev.status }
                : m,
            )
          })

          // Tool events can follow the stop event before the next model turn.
          if (ev.status === 'complete' && ev.stop_reason === 'tool_use') {
            currentMsgUuid.current = ev.message_uuid
          } else {
            if (requestId && retainedSend.current?.requestId === requestId) {
              retainedSend.current = null
              persistRetainedSend(conversationUuid, null)
            }
            cancelRequested.current = false
            isStreamingRef.current = false
            setIsStreaming(false)
            currentMsgUuid.current = null
            activeRequestId.current = null
          }
        },
      ),
    )

    offs.push(
      on(
        'agent:request_status',
        (ev: {
          conversation_uuid: string
          client_request_id: string
          status: 'in_progress' | 'completed'
          request_message_uuid?: string
        }) => {
          if (!conversationUuid || ev.conversation_uuid !== conversationUuid) return
          const retained = retainedSend.current
          if (!retained || ev.client_request_id !== retained.requestId) return
          const pending = pendingSend.current
          if (pending && pending.requestId !== ev.client_request_id) return

          transcriptVersion.current += 1
          needsHistoryRecovery.current = true
          cancelRequested.current = false
          currentMsgUuid.current = null
          activeRequestId.current = null
          isStreamingRef.current = false
          setIsStreaming(false)
          if (pending) {
            pendingSend.current = null
            pending.resolve(ev.status === 'completed')
          }
          setMessages((current) =>
            current.filter(
              (message) =>
                message.uuid !== pending?.optimisticUuid &&
                !(
                  message.clientRequestId === ev.client_request_id &&
                  message.status === 'errored'
                ),
            ),
          )
          if (ev.status === 'completed') {
            retainedSend.current = null
            persistRetainedSend(conversationUuid, null)
          }
          void qc.invalidateQueries({
            queryKey: conversationQueryKey(conversationUuid),
            exact: true,
          })
        },
      ),
    )

    offs.push(
      on(
        'agent:error',
        (ev: {
          conversation_uuid: string
          message?: string
          client_request_id?: string
          request_id?: string
        }) => {
          if (!conversationUuid || ev.conversation_uuid !== conversationUuid) return
          const pending = pendingSend.current
          const requestId = eventRequestId(ev)
          if (pending && requestId && requestId !== pending.requestId) return
          if (!pending && requestId && requestId !== activeRequestId.current) return
          const rejectedSend =
            pending && (!requestId || requestId === pending.requestId) ? pending : null
          if (rejectedSend) {
            pendingSend.current = null
            if (retainedSend.current?.requestId === rejectedSend.requestId) {
              retainedSend.current.authoritative = false
              persistRetainedSend(conversationUuid, retainedSend.current)
            }
            rejectedSend.resolve(false)
          }
          transcriptVersion.current += 1
          cancelRequested.current = false
          const activeUuid = currentMsgUuid.current
          const error = ev.message?.trim() || 'unknown error'
          isStreamingRef.current = false
          setIsStreaming(false)
          currentMsgUuid.current = null
          activeRequestId.current = null
          localMessageSequence.current += 1
          const errorUuid = `err-${Date.now()}-${localMessageSequence.current}`
          setMessages((prev) => {
            const retained = rejectedSend
              ? prev.filter((message) => message.uuid !== rejectedSend.optimisticUuid)
              : prev
            const standaloneError: UIMessage = {
              uuid: errorUuid,
              role: 'assistant',
              blocks: [{ type: 'text', text: `Error: ${error}` }],
              status: 'errored',
              ...(requestId ? { clientRequestId: requestId } : {}),
            }
            if (!activeUuid) return [...retained, standaloneError]

            let found = false
            const updated = retained.map((message) => {
              if (message.uuid !== activeUuid) return message
              found = true
              return {
                ...failMessage(message, error),
                ...(requestId ? { clientRequestId: requestId } : {}),
              }
            })
            return found ? updated : [...retained, standaloneError]
          })
        },
      ),
    )

    return () => {
      active = false
      offs.forEach((off) => off())
      const pending = pendingSend.current
      pendingSend.current = null
      activeRequestId.current = null
      pending?.resolve(false)
    }
  }, [on, emit, conversationUuid, qc])

  const send = useCallback(
    async (content: string, attachments?: SendAttachment[]): Promise<boolean> => {
      if (!conversationUuid || !isConnected || isStreamingRef.current) return false
      const text = content.trim()
      const atts = attachments ?? []
      if (!text && atts.length === 0) return false
      const snapshot = sendSnapshot(text, atts)
      const retained = retainedSend.current
      if (retained?.snapshot === snapshot && retained.authoritative) {
        retainedSend.current = null
        persistRetainedSend(conversationUuid, null)
        return true
      }
      const blocks: UIBlock[] = []
      if (text) blocks.push({ type: 'text', text })
      for (const a of atts) {
        blocks.push({
          type: 'attachment',
          attachment_uuid: a.attachment_uuid,
          filename: a.filename,
          content_type: a.content_type,
          size_bytes: a.size_bytes,
          is_image: a.is_image,
          is_pdf: a.is_pdf,
        })
      }
      // Lock synchronously so a rapid second submit cannot race the server's
      // message_start acknowledgement.
      cancelRequested.current = false
      activeRequestId.current = null
      isStreamingRef.current = true
      setIsStreaming(true)
      transcriptVersion.current += 1
      localMessageSequence.current += 1
      const optimisticUuid = `local-user-${Date.now()}-${localMessageSequence.current}`
      const requestId =
        retained?.snapshot === snapshot
          ? retained.requestId
          : clientRequestId(localMessageSequence.current)
      retainedSend.current = {
        requestId,
        snapshot,
        authoritative: false,
      }
      persistRetainedSend(conversationUuid, retainedSend.current)
      let resolveAcceptance!: (accepted: boolean) => void
      const acceptance = new Promise<boolean>((resolve) => {
        resolveAcceptance = resolve
      })
      pendingSend.current = {
        requestId,
        optimisticUuid,
        resolve: resolveAcceptance,
      }
      setMessages((prev) => [
        ...prev,
        {
          uuid: optimisticUuid,
          role: 'user',
          blocks,
          status: 'complete',
        },
      ])
      // Always send a freshly-acquired MSAL token — the one captured at
      // socket-connect time goes stale after ~1 hour and the backend would
      // reply with "invalid auth".
      try {
        await emitWithAuth('agent:send', {
          conversation_uuid: conversationUuid,
          content: text,
          attachment_uuids: atts.map((a) => a.attachment_uuid),
          client_request_id: requestId,
        })
        return acceptance
      } catch (error: unknown) {
        transcriptVersion.current += 1
        cancelRequested.current = false
        currentMsgUuid.current = null
        activeRequestId.current = null
        isStreamingRef.current = false
        setIsStreaming(false)
        if (pendingSend.current?.requestId === requestId) {
          pendingSend.current = null
          resolveAcceptance(false)
        }
        if (retainedSend.current?.requestId === requestId) {
          retainedSend.current.authoritative = false
          persistRetainedSend(conversationUuid, retainedSend.current)
        }
        const detail = getApiErrorMessage(error, 'Message could not be sent.')
        localMessageSequence.current += 1
        const errorUuid = `err-${Date.now()}-${localMessageSequence.current}`
        setMessages((prev) => [
          ...prev.filter((message) => message.uuid !== optimisticUuid),
          {
            uuid: errorUuid,
            role: 'assistant',
            blocks: [{ type: 'text', text: `Error: ${detail}` }],
            status: 'errored',
          },
        ])
        return false
      }
    },
    [conversationUuid, emitWithAuth, isConnected],
  )

  const cancel = useCallback(() => {
    if (!conversationUuid || !isConnected || !isStreamingRef.current) return
    cancelRequested.current = true
    emit('agent:cancel', { conversation_uuid: conversationUuid })
  }, [conversationUuid, emit, isConnected])

  return useMemo(
    () => ({ messages, send, cancel, isStreaming, isConnected }),
    [messages, send, cancel, isStreaming, isConnected],
  )
}

type StoredToolResult = {
  isError: boolean
  summary: string
}

function storedToolResult(block: Record<string, unknown>): StoredToolResult {
  const isError = block.is_error === true
  let payload: Record<string, unknown> | undefined
  const content = block.content
  if (typeof content === 'string') {
    try {
      const parsed: unknown = JSON.parse(content)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        payload = parsed as Record<string, unknown>
      }
    } catch {
      // Non-JSON tool output is still valid history.
    }
  } else if (content && typeof content === 'object' && !Array.isArray(content)) {
    payload = content as Record<string, unknown>
  }

  const error = typeof payload?.error === 'string' ? payload.error : undefined
  const count = typeof payload?.count === 'number' ? payload.count : undefined
  return {
    isError,
    summary: isError
      ? error
        ? `Error: ${error}`
        : 'Tool failed.'
      : count != null
        ? `${count} results`
        : 'Done',
  }
}

export function storedMessagesToUi(messages: StoredAgentMessage[]): UIMessage[] {
  const toolResults = new Map<string, StoredToolResult>()
  for (const message of messages) {
    for (const block of message.content_blocks) {
      if (block.type !== 'tool_result' || typeof block.tool_use_id !== 'string') continue
      toolResults.set(block.tool_use_id, storedToolResult(block))
    }
  }

  return messages.flatMap((message): UIMessage[] => {
    const blocks = storedBlocksToUi(message.content_blocks, toolResults)
    if (message.role === 'user' && blocks.length === 0) return []
    return [
      {
        uuid: message.uuid,
        role: message.role === 'user' ? 'user' : 'assistant',
        blocks,
        clientRequestId: message.client_request_id,
        status:
          message.status === 'streaming' ||
          message.status === 'errored' ||
          message.status === 'cancelled'
            ? message.status
            : 'complete',
      },
    ]
  })
}

/** Convert stored content_blocks (from REST history) → UIBlock[] */
export function storedBlocksToUi(
  blocks: Array<Record<string, unknown>>,
  toolResults?: ReadonlyMap<string, StoredToolResult>,
): UIBlock[] {
  const out: UIBlock[] = []
  for (const b of blocks ?? []) {
    const t = b.type as string | undefined
    if (t === 'text') {
      out.push({ type: 'text', text: (b.text as string) ?? '' })
    } else if (t === 'thinking') {
      out.push({ type: 'thinking', text: (b.thinking as string) ?? '' })
    } else if (t === 'tool_use') {
      const toolUseId = (b.id as string) ?? ''
      const result = toolResults?.get(toolUseId)
      out.push({
        type: 'tool_use',
        tool_use_id: toolUseId,
        name: (b.name as string) ?? '',
        label: (b.name as string) ?? '',
        status: result ? (result.isError ? 'error' : 'ok') : toolResults ? 'error' : 'ok',
        summary: result?.summary ?? (toolResults ? 'Tool result was not recorded.' : undefined),
      })
    } else if (t === 'agent_attachment') {
      out.push({
        type: 'attachment',
        attachment_uuid: (b.attachment_uuid as string) ?? '',
        filename: (b.filename as string) ?? 'attachment',
        content_type: (b.content_type as string | undefined) ?? null,
        size_bytes: (b.size_bytes as number | undefined) ?? undefined,
        is_image: (b.is_image as boolean | undefined) ?? false,
        is_pdf: (b.is_pdf as boolean | undefined) ?? false,
      })
    }
    // tool_result + redacted_thinking intentionally omitted from UI
  }
  return out
}
